import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FooterType } from "../";
import {
  TemplateType,
  useAnalytics,
  goToWhatApp,
  CategoriesResponseItem,
  useAppLocation,
  WEB_ASSETS,
  makeUrl,
  useStoreImages,
  getEnvVar,
} from "@commons-monorepo/index";
import { FOOTER_EVENTS } from "./events";
import { getPartnerSocials } from "./utils/socials.utils";
import { getPaymentMethods } from "./utils/payments-methods.utils";
import { FooterContext } from "./footer.context";

export const useFooterLogic = () => {
  const { theme, data } = useContext(FooterContext);
  const { t } = useTranslation();
  const { pathname } = useAppLocation();
  const [logEvent] = useAnalytics({ storeMetadata: data?.storeMetadata });
  const { partnerProfilePicture } = useStoreImages(data?.storeMetadata);

  const storeMetadata = data?.storeMetadata;
  const ownerId = storeMetadata.ownerId;
  const hasOffers = data?.hasOffers;
  const hasWholesale = data?.hasWholesale;
  const hasServices = data?.hasServices;
  const partnerNetwork = storeMetadata?.userNetwork;
  const androidLogo = `${WEB_ASSETS}/fonts/img/app-android-download.png`;
  const appleLogo = `${WEB_ASSETS}/fonts/img/app-apple-download.png`;
  const showSic = storeMetadata.country === "Colombia";
  const imageSicWhite = `${WEB_ASSETS}/sic-white.png`;
  const imageSicBlack = `${WEB_ASSETS}/sic-black.png`;

  const addressActive = storeMetadata.userAddresses?.find(
    (address) => address.active,
  );
  const scheduleResume = storeMetadata.scheduleConfiguration?.resume;
  const footerConfig = theme.config.footer;
  const lightMode = !footerConfig?.darkMode;

  const partnerSocials = getPartnerSocials(partnerNetwork);
  const paymentMethods = getPaymentMethods(storeMetadata);

  const catalogueHomeUrl = useMemo(() => {
    return `${pathname}/${makeUrl(pathname, "")}`;
  }, [pathname]);

  const termsConditionsUrl = useMemo(() => {
    return `${pathname}/${makeUrl(pathname, "terminos-y-condiciones")}`;
  }, [pathname]);

  const termsConditionsExternalUrl = useMemo(() => {
    if (ownerId === "b16ff7bb594ccb296192699429c252d908ce1837")
      return "https://drive.google.com/file/d/10tGHngOQZ_1scd47Gqio6jfqHkXoNu5Q/view";
    return;
  }, [ownerId]);

  const categories: { name: string }[] = useMemo(() => {
    return [...((data?.categories as CategoriesResponseItem[]) || [])]
      .sort((c) => (c.subCategories?.length > 0 ? -1 : 1))
      .map((item) => ({ name: item.category.name }));
  }, [data]);

  const handleSocialButton = (source: string) => {
    logEvent(FOOTER_EVENTS.FOOTER_SELECT_SOCIAL_MEDIA, { source });
  };

  const handleGoLanding = () => {
    logEvent(FOOTER_EVENTS.FOOTER_SELECT_GO_LANDING);
  };

  const handleGoToApp = (source: "Appstore" | "Playstore") => {
    logEvent(FOOTER_EVENTS.FOOTER_SELECT_APP_STORE, { source });
  };

  const contactToShop = () => {
    logEvent(FOOTER_EVENTS.SELECT_CONTACT_VENDOR, {
      source: "support_request",
    });
    goToWhatApp(
      `${storeMetadata?.prefixPhone}${storeMetadata?.phone}`,
      t("footer.text_whats_app_store", {
        link: encodeURIComponent(window.location.href),
      }),
    );
  };

  const contactToSumer = () => {
    logEvent(FOOTER_EVENTS.SELECT_SUPPORT_REQUEST);
    goToWhatApp(
      t("support_phone"),
      t("footer.text_whats_app_sumer", {
        link: encodeURIComponent(catalogueHomeUrl),
      }),
    );
  };

  const footerTypeClass = useMemo(() => {
    switch (theme.template.FOOTER) {
      case TemplateType.FOOTER_1:
        return FooterType.Type1;
      case TemplateType.FOOTER_2:
        return FooterType.Type2;
      default:
        return FooterType.Type1;
    }
  }, [theme]);

  return {
    t,
    termsConditionsUrl,
    termsConditionsExternalUrl,
    storeMetadata,
    footerTypeClass,
    catalogueHomeUrl,
    partnerProfilePicture,
    partnerSocials,
    categories,
    footerConfig,
    pathname,
    addressActive,
    scheduleResume,
    showSic,
    handleSocialButton,
    handleGoLanding,
    handleGoToApp,
    contactToShop,
    contactToSumer,
    logEvent,
    hasOffers,
    hasWholesale,
    hasServices,
    androidLogo,
    appleLogo,
    imageSicWhite,
    imageSicBlack,
    paymentMethods,
    lightMode,
  };
};
